import { Viewer, Worker, RotateDirection, SpecialZoomLevel } from '@react-pdf-viewer/core';
import { toolbarPlugin, type ToolbarSlot } from '@react-pdf-viewer/toolbar';
import { printPlugin } from '@react-pdf-viewer/print';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/toolbar/lib/styles/index.css';
import '@react-pdf-viewer/print/lib/styles/index.css';
import { ZoomIn, ZoomOut, RotateCw, Printer, Download, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface PDFViewerProps {
  fileUrl: string;
  className?: string;
  withCredentials?: boolean;
  onDownload?: () => void;
  onDelete?: (() => void) | null;
}

export function PDFViewer({
  fileUrl,
  className,
  withCredentials = false,
  onDownload,
  onDelete,
}: PDFViewerProps): React.ReactElement {
  const toolbarPluginInstance = toolbarPlugin();
  const printPluginInstance = printPlugin();
  const { Toolbar } = toolbarPluginInstance;

  return (
    <div className={`relative flex flex-col ${className ?? ''}`}>
      <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
        <Toolbar>
          {(slot: ToolbarSlot) => (
            <div className="flex items-center px-2 py-1.5 bg-muted/40 shrink-0">
              {/* Left: page indicator */}
              <div className="flex items-center gap-1 text-xs text-muted-foreground w-20">
                <slot.CurrentPageLabel>
                  {(props: { currentPage: number }) => <span>{props.currentPage + 1}</span>}
                </slot.CurrentPageLabel>
                <span>/</span>
                <slot.NumberOfPages>
                  {(props: { numberOfPages: number }) => <span>{props.numberOfPages}</span>}
                </slot.NumberOfPages>
              </div>

              {/* Center: zoom & rotate */}
              <div className="flex-1 flex items-center justify-center gap-1">
                <slot.ZoomOut>
                  {(props: { onClick: () => void }) => (
                    <Button onClick={props.onClick} size="icon" variant="ghost" className="h-7 w-7">
                      <ZoomOut size={16} />
                    </Button>
                  )}
                </slot.ZoomOut>

                <slot.ZoomIn>
                  {(props: { onClick: () => void }) => (
                    <Button onClick={props.onClick} size="icon" variant="ghost" className="h-7 w-7">
                      <ZoomIn size={16} />
                    </Button>
                  )}
                </slot.ZoomIn>

                <div className="w-px h-4 bg-border mx-1" />

                <slot.Rotate direction={RotateDirection.Forward}>
                  {(props: { onClick: () => void }) => (
                    <Button onClick={props.onClick} size="icon" variant="ghost" className="h-7 w-7">
                      <RotateCw size={16} />
                    </Button>
                  )}
                </slot.Rotate>
              </div>

              {/* Right: print, download & delete */}
              <div className="flex items-center gap-1 w-24 justify-end">
                <slot.Print>
                  {(props: { onClick: () => void }) => (
                    <Button
                      onClick={props.onClick}
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      title="Print"
                    >
                      <Printer size={16} />
                    </Button>
                  )}
                </slot.Print>

                {onDownload != null && (
                  <Button
                    onClick={onDownload}
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    title="Download"
                  >
                    <Download size={16} />
                  </Button>
                )}

                {onDelete != null && (
                  <Button
                    onClick={onDelete}
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-destructive hover:text-destructive"
                  >
                    <Trash2 size={16} />
                  </Button>
                )}
              </div>
            </div>
          )}
        </Toolbar>

        <div className="relative flex-1 min-h-0 bg-muted/40">
          <div className="absolute inset-0">
            <Viewer
              fileUrl={fileUrl}
              withCredentials={withCredentials}
              defaultScale={SpecialZoomLevel.PageWidth}
              plugins={[toolbarPluginInstance, printPluginInstance]}
            />
          </div>
        </div>
      </Worker>
    </div>
  );
}
